import { LOGIN_TEMPLATE } from '@bridge/constants';

import Sentry from 'utils/sentry';
import { AUTH_SESSION_CONFIG, TOKEN_TYPES } from '../_constants/auth.constants';

import type { StytchB2BClient } from '@stytch/nextjs/b2b';
import type { TokenType } from '../_constants/auth.constants';

/**
 * Error thrown when a user's organization requires a different auth method than the one used.
 */
export class AuthMethodRestrictedError extends Error {
  allowedAuthMethods: string[];
  constructor(allowedAuthMethods: string[]) {
    super('Authentication method not allowed for this organization');
    this.name = 'AuthMethodRestrictedError';
    this.allowedAuthMethods = allowedAuthMethods;
  }
}

/** Projected shape of a Stytch discovered organization used by the picker UI. */
export interface DiscoveredOrg {
  organizationId: string;
  organizationName: string;
  memberAuthenticated: boolean;
  /** Auth methods the org requires when the current session can't be used directly. Empty when no restriction. */
  primaryRequiredAuthMethods: string[];
  /**
   * The org's SSO connection to start when it's locked behind SSO — its default connection,
   * falling back to the first active one. Null when the org exposes no SSO connection, in which
   * case the picker can't offer an SSO sign-in for it.
   */
  ssoConnectionId: string | null;
  /** Member's email, used to send a magic link. Null when the membership carries no member. */
  memberEmailAddress: string | null;
}

// `primary_required.allowed_auth_methods` reflects the org's auth policy, not which connections are
// provisioned — an org can report `sso` as allowed while having no SSO connection configured.
const SSO_AUTH_METHOD = 'sso';
const MAGIC_LINK_AUTH_METHOD = 'magic_link';

/** SSO is required and we resolved a connection to start. False when SSO has no connection. */
export function canStartSsoForOrg(org: DiscoveredOrg): org is DiscoveredOrg & { ssoConnectionId: string } {
  return org.primaryRequiredAuthMethods.includes(SSO_AUTH_METHOD) && Boolean(org.ssoConnectionId);
}

/**
 * SSO can't be started but the org accepts magic links and we know the member's email — the path
 * for orgs whose policy lists SSO with no connection provisioned. Check only after
 * {@link canStartSsoForOrg} (SSO is preferred when available).
 */
export function canReauthWithMagicLink(org: DiscoveredOrg): org is DiscoveredOrg & { memberEmailAddress: string } {
  return (
    !canStartSsoForOrg(org) &&
    org.primaryRequiredAuthMethods.includes(MAGIC_LINK_AUTH_METHOD) &&
    Boolean(org.memberEmailAddress)
  );
}

/**
 * A row the picker can act on: directly selectable (no restriction), or reachable via SSO / magic
 * link re-auth. Shared by the picker's disabled state and the discovery gate so an all-restricted
 * set that's still magic-link-reachable reaches the picker instead of bouncing to /login.
 */
export function isOrgActionable(org: DiscoveredOrg): boolean {
  return org.primaryRequiredAuthMethods.length === 0 || canStartSsoForOrg(org) || canReauthWithMagicLink(org);
}

/**
 * Which downstream Stytch call the picker must use to land the chosen org.
 * `intermediate_session` — magic-link / OAuth discovery returned an IST; picker calls
 *   `discovery.intermediateSessions.exchange`.
 * `sso_session` — SSO auth already minted a full session into Stytch's auto-selected
 *   org; picker calls `session.exchange` to swap into the chosen org.
 */
export type OrgSelectionSource = 'intermediate_session' | 'sso_session';

export type StytchAuthResult =
  | { type: 'session'; data: Record<string, unknown> }
  | { type: 'discovery'; source: OrgSelectionSource; orgs: DiscoveredOrg[] };

/**
 * True when an SSO return originated from picking an org in the selector (`pickedOrgId`)
 * and the handshake landed a session that can access it directly — so the authenticate
 * page can complete straight in rather than re-showing the picker. Stays false if Stytch
 * landed the session in a different org (the picked org comes back still restricted).
 */
export function canAutoCompletePickedOrg(result: StytchAuthResult, pickedOrgId?: string): boolean {
  if (result.type !== 'discovery' || result.source !== 'sso_session' || !pickedOrgId) return false;
  return result.orgs.some((org) => org.organizationId === pickedOrgId && org.primaryRequiredAuthMethods.length === 0);
}

interface RawDiscoveredOrganization {
  organization: {
    organization_id: string;
    organization_name: string;
    sso_default_connection_id?: string | null;
    sso_active_connections?: { connection_id: string }[];
  };
  member_authenticated?: boolean;
  primary_required?: { allowed_auth_methods?: string[] } | null;
  membership?: { type?: string; member?: { email_address?: string } | null } | null;
}

/**
 * Authenticates a magic link token for direct organization login.
 */
async function authenticateMagicLink(stytch: StytchB2BClient, token: string) {
  return stytch.magicLinks.authenticate({
    magic_links_token: token,
    ...AUTH_SESSION_CONFIG,
  });
}

/**
 * Starts an SSO connection's hosted flow, sending the login and signup returns to the
 * same URL. Omitting the redirect overrides falls back to Stytch's configured default.
 */
export function startSsoConnection(stytch: StytchB2BClient, connectionId: string, redirectUrl?: string): void {
  stytch.sso.start({
    connection_id: connectionId,
    ...(redirectUrl ? { login_redirect_url: redirectUrl, signup_redirect_url: redirectUrl } : {}),
  });
}

/**
 * Shared by the login form and the org picker so the redirect target and template live in one
 * place. Callers must check `status_code` before navigating to the confirmation page.
 */
export function sendDiscoveryMagicLink(stytch: StytchB2BClient, emailAddress: string, discoveryRedirectUrl: string) {
  return stytch.magicLinks.email.discovery.send({
    discovery_redirect_url: discoveryRedirectUrl,
    email_address: emailAddress,
    login_template_id: LOGIN_TEMPLATE,
  });
}

/**
 * Authenticates an SSO token for single sign-on.
 */
async function authenticateSSO(stytch: StytchB2BClient, token: string) {
  return stytch.sso.authenticate({
    sso_token: token,
    ...AUTH_SESSION_CONFIG,
  });
}

/**
 * Authenticates a discovery magic link token and returns discovered organizations.
 */
async function authenticateDiscoveryMagicLink(stytch: StytchB2BClient, token: string) {
  return stytch.magicLinks.discovery.authenticate({
    discovery_magic_links_token: token,
  });
}

/**
 * Authenticates a discovery OAuth token and returns discovered organizations.
 */
async function authenticateDiscoveryOAuth(stytch: StytchB2BClient, token: string) {
  return stytch.oauth.discovery.authenticate({
    discovery_oauth_token: token,
  });
}

/**
 * Exchanges a discovery intermediate session for an organization session.
 */
export function exchangeDiscoveryToken(stytch: StytchB2BClient, organizationId: string) {
  return stytch.discovery.intermediateSessions.exchange({
    organization_id: organizationId,
    ...AUTH_SESSION_CONFIG,
  });
}

/**
 * Swaps the member's current Stytch session for a session in another organization
 * they belong to. Used after SSO auth when Stytch auto-selected the "owning" org
 * but the user wants a different one.
 *
 * See https://stytch.com/docs/multi-tenant-auth/manage-sessions/lifecycle/exchange-session
 */
export function exchangeSessionForOrg(stytch: StytchB2BClient, organizationId: string) {
  return stytch.session.exchange({
    organization_id: organizationId,
    ...AUTH_SESSION_CONFIG,
  });
}

/**
 * Maps token types to their respective authentication handlers.
 */
const AUTH_HANDLERS = {
  [TOKEN_TYPES.MAGIC_LINK]: authenticateMagicLink,
  [TOKEN_TYPES.SSO]: authenticateSSO,
  [TOKEN_TYPES.DISCOVERY_MAGIC_LINK]: authenticateDiscoveryMagicLink,
  [TOKEN_TYPES.DISCOVERY_OAUTH]: authenticateDiscoveryOAuth,
};

function toDiscoveredOrg(raw: RawDiscoveredOrganization): DiscoveredOrg {
  return {
    organizationId: raw.organization.organization_id,
    organizationName: raw.organization.organization_name,
    memberAuthenticated: Boolean(raw.member_authenticated),
    primaryRequiredAuthMethods: raw.primary_required?.allowed_auth_methods ?? [],
    ssoConnectionId:
      raw.organization.sso_default_connection_id ?? raw.organization.sso_active_connections?.[0]?.connection_id ?? null,
    memberEmailAddress: raw.membership?.member?.email_address ?? null,
  };
}

/**
 * Picks the best org from a single-org discovery response and exchanges its token,
 * preserving the original auto-selection rules for the common single-org case.
 */
async function autoSelectAndExchange(stytch: StytchB2BClient, orgs: RawDiscoveredOrganization[]) {
  // Find an org where primary auth is satisfied (member_authenticated: true)
  const authenticatedOrg = orgs.find((org) => org.member_authenticated);
  if (authenticatedOrg) {
    return exchangeDiscoveryToken(stytch, authenticatedOrg.organization.organization_id);
  }

  // When MFA is required, Stytch returns member_authenticated: false even though
  // primary auth succeeded. Look for an org that only needs secondary auth (MFA),
  // not a primary auth method restriction. The exchange will return the mfa_required
  // response which checkMfaRequired() in the authenticate page handles.
  const orgNeedingMfa = orgs.find((org) => !org.primary_required?.allowed_auth_methods?.length);
  if (orgNeedingMfa) {
    return exchangeDiscoveryToken(stytch, orgNeedingMfa.organization.organization_id);
  }

  // All orgs require a different primary auth method (e.g., SSO)
  const orgWithRestriction = orgs.find((org) => org.primary_required?.allowed_auth_methods?.length);
  if (orgWithRestriction) {
    throw new AuthMethodRestrictedError(orgWithRestriction.primary_required?.allowed_auth_methods ?? []);
  }

  throw new Error('No member authenticated organization found');
}

/**
 * Inspects the SSO authenticate response for a fully-authenticated member; if the user
 * belongs to additional orgs, returns a discovery payload so the picker can run.
 *
 * Stytch deduplicates external SSO connections by email and auto-selects an "owning"
 * org, which means a developer with the same email in test + prod orgs always lands
 * in the same one regardless of which IdP tile they clicked. See
 * https://stytch.com/docs/multi-tenant-auth/authentication/sso/multi-organization.
 *
 * Failures of the secondary `discovery.organizations.list` call are logged and
 * swallowed — they must not prevent a successful SSO login.
 */
async function maybeDiscoverPostSsoOrgs(
  stytch: StytchB2BClient,
  ssoResponse: Record<string, unknown>
): Promise<StytchAuthResult> {
  const sessionResult: StytchAuthResult = { type: 'session', data: ssoResponse };

  // Skip discovery if Stytch did not actually issue a session (MFA / primary_required).
  // The existing MFA flow handles that case; switching orgs requires a full session.
  if (ssoResponse.member_authenticated !== true) {
    return sessionResult;
  }

  let listResponse: { discovered_organizations?: RawDiscoveredOrganization[] };
  try {
    listResponse = (await stytch.discovery.organizations.list()) as unknown as typeof listResponse;
  } catch (error) {
    Sentry.captureException(error);
    return sessionResult;
  }

  // active_member only — pending/invited memberships aren't valid targets for session.exchange.
  // Guard against malformed entries (missing organization, missing id) so a partial response
  // can't crash the whole SSO login inside .map below.
  const memberships = (listResponse.discovered_organizations ?? []).filter(
    (org) => org.membership?.type === 'active_member' && org.organization?.organization_id
  );

  if (memberships.length < 2) {
    return sessionResult;
  }

  return { type: 'discovery', source: 'sso_session', orgs: memberships.map(toDiscoveredOrg) };
}

/**
 * Main authentication handler that routes to the appropriate strategy based on token type.
 *
 * Returns a `session` payload for direct logins and single-org discovery responses
 * (auto-selected for backwards compatibility), and a `discovery` payload listing the
 * orgs the user belongs to when there are multiple — the caller is expected to show
 * a picker before exchanging an intermediate session token.
 */
export async function authenticateWithStytch(
  stytch: StytchB2BClient,
  token: string,
  tokenType: string
): Promise<StytchAuthResult> {
  const handler = AUTH_HANDLERS[tokenType as TokenType];

  if (!handler) {
    throw new Error(`Unsupported token type: ${tokenType}`);
  }

  const response = await handler(stytch, token);

  if (!('discovered_organizations' in response)) {
    // SSO returns a full session for one org. If the member belongs to multiple,
    // surface the picker so they can switch.
    if (tokenType === TOKEN_TYPES.SSO) {
      return maybeDiscoverPostSsoOrgs(stytch, response as unknown as Record<string, unknown>);
    }
    return { type: 'session', data: response as unknown as Record<string, unknown> };
  }

  const discovered = (response.discovered_organizations ?? []) as RawDiscoveredOrganization[];

  if (discovered.length === 0) {
    throw new Error('No discovered organizations found');
  }

  if (discovered.length > 1) {
    const orgs = discovered.map(toDiscoveredOrg);
    // When no row is actionable, the picker would render every row disabled — surface
    // AuthMethodRestrictedError so the caller can show authMethodRestrictedToast and route back to
    // /login.
    if (!orgs.some(isOrgActionable)) {
      throw new AuthMethodRestrictedError(orgs[0]?.primaryRequiredAuthMethods ?? []);
    }
    return { type: 'discovery', source: 'intermediate_session', orgs };
  }

  const session = await autoSelectAndExchange(stytch, discovered);
  return { type: 'session', data: session as unknown as Record<string, unknown> };
}
